import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { formatUnits } from "viem";
import { ExternalLink, Coins } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { fmtNum } from "../lib/format";
import { txUrl } from "../lib/chains";
import { TokenLogo } from "./TokenSelect";

const usd = (v) => (v == null ? "—" : `$${Number(v).toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`);

// Creator share of HIVEVORA swap fees, read from confirmed swap receipts on Robinhood Chain.
export const CreatorEarnings = ({ wallet, isMe }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { api.get(`/earnings/${wallet}`).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e))); }, [wallet]);
  if (err) return null;
  if (!d || (!d.tokens.length && !isMe)) return null;
  return (
    <section data-testid="creator-earnings" className="glass rounded-2xl p-5 space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-500">Creator swap-fee earnings</p>
          <p className="font-display text-3xl font-black text-lime" data-testid="creator-earnings-total">{usd(d.total_usd)}</p>
        </div>
        <p className="text-[11px] text-slate-500 max-w-sm">{isMe ? "Your" : "This creator's"} share of the HIVEVORA swap fee, paid to {isMe ? "your" : "their"} wallet on-chain in each swap. USD at the time of each swap.</p>
      </div>
      {!d.tokens.length ? <p className="text-xs text-slate-400" data-testid="creator-earnings-empty">No swap-fee earnings yet. Earnings appear here after people swap your listed or launched tokens on HIVEVORA.</p> : (
        <div className="space-y-2">
          {d.tokens.map((t) => (
            <div key={t.fee_token} data-testid={`earnings-token-${t.fee_token}`} className="flex flex-wrap items-center gap-3 rounded-xl bg-black/30 px-3 py-2.5">
              <TokenLogo t={{ address: t.fee_token, symbol: t.symbol || "?", logo: t.logo }} size={28} />
              <Link to={`/token/${t.fee_token}`} className="min-w-0 flex-1 font-semibold text-white hover:text-lime truncate">{t.symbol || `${t.fee_token.slice(0, 8)}…`}</Link>
              <span className="text-[11px] text-slate-400 font-mono">{t.assets.map((a) => `${fmtNum(formatUnits(BigInt(a.amount), a.decimals), 6)} ${a.symbol}`).join(" + ")}</span>
              <span className="text-xs text-slate-500">{t.swaps} swaps</span>
              <span className="font-mono text-sm text-lime">{usd(t.usd)}</span>
            </div>
          ))}
          <p className="pt-2 text-[10px] uppercase tracking-widest text-slate-500">Recent payouts</p>
          {d.recent.map((r) => (
            <a key={r.tx_hash} href={txUrl(r.tx_hash, 4663)} target="_blank" rel="noreferrer" data-testid={`earnings-tx-${r.tx_hash}`}
              className="flex items-center gap-2 text-[11px] text-slate-400 hover:text-lime">
              <Coins className="h-3 w-3" /> {r.day} · {fmtNum(formatUnits(BigInt(r.amount), r.decimals), 6)} {r.asset_symbol}{r.exact ? "" : " (quoted)"} · {usd(r.usd)}
              <span className="font-mono">{r.tx_hash.slice(0, 10)}…</span><ExternalLink className="h-3 w-3" />
            </a>
          ))}
        </div>
      )}
    </section>
  );
};
